import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "react-toastify";
import { MdUnfoldMore } from "react-icons/md";

import { useAuth } from "../auth/AuthContext";
import { useActiveBusiness } from "../auth/useActiveBusiness";
import { roleOf } from "../auth/permissions";
import { brandFor } from "../businessBranding";
import { hideSwitchCover, showSwitchCover } from "../businessSwitchCover";
import "../business-switcher.css";

/* ------------------------------------------------------------------ *
 * Business switcher — the foot of the sidebar, for anyone who belongs
 * to more than one business (an admin managing two shops, say).
 *
 * Picking a business goes through POST /auth/switch-business, so the
 * session's role and permissions become the ones that person holds in
 * the new business — an admin never picks up an owner's view (revenue
 * stays hidden). Everything on screen belongs to the old business, so
 * the page reloads into the new one — behind a full-page cover that stays
 * up until the new business's data has arrived (businessSwitchCover.js).
 *
 * The menu is portalled to <body>: the sidebar clips its overflow and
 * collapses to a 72px rail, and the list has to escape both.
 * ------------------------------------------------------------------ */

const ROLE_SHORT = { owner: "Owner", admin: "Admin", staff: "Florist", driver: "Driver" };

function Mark({ business, small }) {
  const brand = brandFor(business);
  return (
    <span className={`rf-bsw-mark${small ? " is-small" : ""}`} aria-hidden="true">
      {brand.logo ? <img src={brand.logo} alt="" /> : brand.monogram}
    </span>
  );
}

/* Pages that make sense in any business — the nav destinations. Anything
   else (an order, a transfer) belongs to the old business, so the switch
   lands on the dashboard instead. */
const STAY_ON = [
  "/",
  "/work-orders",
  "/ordered-products",
  "/delivery-runs",
  "/transfers",
  "/transfers/outgoing",
  "/products",
  "/funeral-directors",
  "/staff",
  "/settings",
];

export default function BusinessSwitcher() {
  const { memberships, switchBusiness } = useAuth() || {};
  const active = useActiveBusiness();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState("");
  const [pos, setPos] = useState(null);
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  const list = (memberships || []).map((m) => ({
    id: String(m.business_id),
    name: m.business_name || m.business_code || "Business",
    code: m.business_code || "",
    role: ROLE_SHORT[roleOf(m)] || "",
  }));
  const current =
    list.find((b) => active && b.id === String(active.id)) ||
    (active ? { id: String(active.id), name: active.name || "Business", code: active.code || "", role: "" } : null);

  /* the menu opens upward from the button, left-aligned with it */
  useLayoutEffect(() => {
    if (!open || !btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    setPos({ left: r.left, bottom: window.innerHeight - r.top + 6 });
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (btnRef.current && btnRef.current.contains(e.target)) return;
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    const onResize = () => setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", onResize);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", onResize);
    };
  }, [open]);

  if (list.length < 2 || !current) return null;

  async function pick(b) {
    if (busy) return;
    if (b.id === current.id) {
      setOpen(false);
      return;
    }
    setBusy(b.id);
    setOpen(false);
    /* hide the old business at once — nothing of it should show while the
       switch and the reload happen */
    const brand = brandFor(b);
    showSwitchCover({ name: b.name, logo: brand.logo, monogram: brand.monogram });
    try {
      const res = await switchBusiness(b.id);
      if (!res || !res.ok) throw new Error((res && res.error) || "");
      const path = window.location.pathname;
      window.location.assign(STAY_ON.includes(path) ? path : "/");
    } catch (e) {
      setBusy("");
      hideSwitchCover();
      /* the server's reason ("You are not a member of that business"),
         not axios's "Request failed with status code 403" */
      const reason = e && e.response && e.response.data && e.response.data.message;
      toast.error(reason || `Could not switch to ${b.name}`);
    }
  }

  return (
    <div className="rf-sidebar-business">
      <div className="rf-bsw-eyebrow">Business</div>
      <button
        ref={btnRef}
        type="button"
        className={`rf-bsw-btn${open ? " is-open" : ""}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        title={`${current.name} — switch business`}
        onClick={() => setOpen((o) => !o)}
      >
        <Mark business={current} />
        <span className="rf-bsw-text">
          <span className="rf-bsw-name">{current.name}</span>
          {current.role && <span className="rf-bsw-role">{current.role}</span>}
        </span>
        <MdUnfoldMore className="rf-bsw-caret" />
      </button>

      {open &&
        pos &&
        createPortal(
          <div
            ref={menuRef}
            className="rf-bsw-menu"
            role="listbox"
            aria-label="Switch business"
            style={{ left: pos.left, bottom: pos.bottom }}
          >
            <div className="rf-bsw-menu-head">Switch business</div>
            {list.map((b) => {
              const on = b.id === current.id;
              return (
                <button
                  key={b.id}
                  type="button"
                  role="option"
                  aria-selected={on}
                  className={`rf-bsw-opt${on ? " is-current" : ""}`}
                  disabled={Boolean(busy)}
                  onClick={() => pick(b)}
                >
                  <Mark business={b} small />
                  <span className="rf-bsw-text">
                    <span className="rf-bsw-name">{b.name}</span>
                    {b.role && <span className="rf-bsw-role">{b.role}</span>}
                  </span>
                  <span className="rf-bsw-tick" aria-hidden="true">
                    {busy === b.id ? "…" : on ? "✓" : ""}
                  </span>
                </button>
              );
            })}
          </div>,
          document.body
        )}
    </div>
  );
}
